import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";

import { pathGenerators } from "utils/routes";

import { formatCurrency, formatDate, getPaymentPill } from "../constants";

export const usePaymentsColumns = ({
  handlePatientClick,
  handlePaymentClick,
  handleDownloadReceipt,
}) => {
  const columns = useMemo(
    () => [
      {
        id: "consultationDate",
        header: "Consultation Date",
        width: "130px",
        render: (p) =>
          formatDate(p.consultation?.consultationDate || p.consultationDate),
      },
      {
        id: "patientName",
        header: "Patient Name",
        width: "minmax(180px, 1fr)",
        render: (p) => (
          <Link
            to={pathGenerators.patient.payments(p.patient?.id)}
            style={{
              color: "var(--green-700)",
              textDecoration: "none",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              overflow: "hidden",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            onClick={(e) => handlePatientClick(e, p.patient)}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {p.patient?.firstName || ""} {p.patient?.surname || ""}
            </span>
          </Link>
        ),
      },
      {
        id: "status",
        header: "Status",
        width: "100px",
        render: (p) => getPaymentPill(p.status),
      },
      {
        id: "amountPaid",
        header: "Paid",
        width: "90px",
        render: (p) => (
          <strong style={{ color: "#006546" }}>
            {formatCurrency(p.totalAmountPaid)}
          </strong>
        ),
      },
      {
        id: "moneyOwed",
        header: "Owed",
        width: "90px",
        render: (p) => (
          <strong style={{ color: p.moneyOwed > 0 ? "#b70000" : "#006546" }}>
            {formatCurrency(p.moneyOwed)}
          </strong>
        ),
      },
      {
        id: "totalDue",
        header: "Total Due",
        width: "90px",
        render: (p) => <strong>{formatCurrency(p.totalAmountDue)}</strong>,
      },
      {
        id: "daysOpen",
        header: "Days",
        width: "70px",
        render: (p) => p.daysOpen || "-",
      },
      {
        id: "totalServices",
        header: "Services",
        width: "80px",
        render: (p) => p.numberOfPaymentsDue || 0,
      },
      {
        id: "lastPayment",
        header: "Last Payment",
        width: "110px",
        render: (p) => formatDate(p.lastPaymentDate),
      },
    ],
    [handlePatientClick]
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Pay",
        primary: true,
        visibleWhen: (p) => p.status !== "PAID" && p.moneyOwed > 0,
        onClick: (p) => handlePaymentClick(p),
      },
      {
        label: "View",
        primary: true,
        visibleWhen: (p) => p.status === "PAID" && p.totalAmountPaid > 0,
        onClick: (p) => handlePaymentClick(p),
      },
      {
        label: "Receipt",
        icon: Download,
        primary: true,
        onClick: (p) => handleDownloadReceipt(p),
      },
    ],
    [handlePaymentClick, handleDownloadReceipt]
  );

  return { columns, rowActions };
};
